export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
}

export interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

// Raw Telegram WebApp object shape
interface TgWebApp {
  version: string;
  initData: string;
  initDataUnsafe?: { user?: TelegramUser };
  ready: () => void;
  expand: () => void;
  close: () => void;
  requestFullscreen?: () => void;
  exitFullscreen?: () => void;
  isFullscreen?: boolean;
  lockOrientation?: () => void;
  unlockOrientation?: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  disableVerticalSwipes?: () => void;
  colorScheme: 'light' | 'dark';
  headerHeight: number;
  viewportHeight: number;
  viewportStableHeight: number;
  isExpanded: boolean;
  safeAreaInset?: { top?: number; bottom?: number; left?: number; right?: number };
  contentSafeAreaInset?: { top?: number; bottom?: number; left?: number; right?: number };
  onEvent: (eventType: string, callback: (data?: unknown) => void) => void;
  offEvent?: (eventType: string, callback: (data?: unknown) => void) => void;
  isVersionAtLeast?: (version: string) => boolean;
  switchInlineQuery?: (query: string, choose_chat_types?: string[]) => void;
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
  openTelegramLink?: (url: string) => void;
  showAlert?: (message: string, callback?: (confirmed: boolean) => void) => void;
  showConfirm?: (message: string, callback?: (confirmed: boolean) => void) => void;
  showPopup?: (params: {
    title?: string;
    message: string;
    buttons?: Array<{ type: string; text: string }>;
  }, callback?: (buttonId: string) => void) => void;
  HapticFeedback?: {
    impactOccurred: (style: string) => void;
    notificationOccurred: (type: string) => void;
    selectionChanged: () => void;
  };
  MainButton?: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    isProgressVisible: boolean;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
    setText: (text: string) => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
  };
}

function getWa(): TgWebApp | undefined {
  return (window as { Telegram?: { WebApp?: TgWebApp } }).Telegram?.WebApp;
}

export class TelegramAdapter {
  private isTelegram: boolean;
  private initData: string | null = null;
  private _isFullscreen = false;

  constructor() {
    this.isTelegram = typeof window !== 'undefined' && !!getWa();
    if (this.isTelegram) {
      this.init();
    }
  }

  private init(): void {
    const wa = getWa();
    if (!wa) return;

    wa.ready();
    wa.expand();

    // Set dark theme colors
    try {
      wa.setHeaderColor?.('#1a1a2e');
      wa.setBackgroundColor?.('#1a1a2e');
    } catch { /* noop */ }

    this.initData = wa.initData;

    // Listen for fullscreen changes
    wa.onEvent('fullscreenChanged', () => {
      this._isFullscreen = !!wa.isFullscreen;
      console.log('[TG] fullscreenChanged:', this._isFullscreen);
    });

    wa.onEvent('fullscreenFailed', (data) => {
      console.warn('[TG] fullscreenFailed:', data);
    });

    // Listen for viewport changes (orientation, resize)
    wa.onEvent('viewport_changed', (data) => {
      const vp = data as { viewportHeight?: number; isStateStable?: boolean } | undefined;
      console.log('[TG] viewport_changed:', vp?.viewportHeight, 'stable:', vp?.isStateStable);
      // Dispatch custom event for Game.ts to handle
      window.dispatchEvent(new Event('resize'));
    });

    if (wa.initDataUnsafe?.user) {
      console.log('[TG] user:', wa.initDataUnsafe.user);
    }
  }

  getIsTelegram(): boolean {
    return this.isTelegram;
  }

  getInitData(): string | null {
    return this.initData ?? getWa()?.initData ?? null;
  }

  getUser(): TelegramUser | null {
    if (!this.isTelegram) return null;
    return getWa()?.initDataUnsafe?.user as TelegramUser ?? null;
  }

  getSafeAreaInsets(): SafeAreaInsets {
    const wa = getWa();
    if (!wa) return { top: 0, bottom: 0, left: 0, right: 0 };
    const insets = wa.safeAreaInset ?? {};
    return {
      top: insets.top ?? 0,
      bottom: insets.bottom ?? 0,
      left: insets.left ?? 0,
      right: insets.right ?? 0,
    };
  }

  getContentSafeAreaInsets(): SafeAreaInsets {
    const wa = getWa();
    if (!wa) return { top: 0, bottom: 0, left: 0, right: 0 };
    const insets = wa.contentSafeAreaInset ?? {};
    return {
      top: insets.top ?? 0,
      bottom: insets.bottom ?? 0,
      left: insets.left ?? 0,
      right: insets.right ?? 0,
    };
  }

  getHeaderHeight(): number {
    if (!this.isTelegram) return 0;
    return getWa()?.headerHeight ?? 0;
  }

  getViewportHeight(): number {
    return getWa()?.viewportHeight ?? window.innerHeight;
  }

  getViewportWidth(): number {
    return window.innerWidth;
  }

  colorScheme(): 'light' | 'dark' {
    return getWa()?.colorScheme ?? 'dark';
  }

  onThemeChanged(callback: (scheme: 'light' | 'dark') => void): void {
    const wa = getWa();
    if (!wa) return;
    wa.onEvent('themeChanged', () => callback(this.colorScheme()));
  }

  disableVerticalSwipe(): void {
    const wa = getWa();
    if (!wa) return;
    try { wa.disableVerticalSwipes?.(); } catch { /* noop */ }
  }

  close(): void {
    getWa()?.close();
  }

  // ─── Fullscreen (Telegram Bot API 8.0+) ───

  requestFullscreen(): boolean {
    const wa = getWa();
    if (!wa) return false;

    // Check Telegram version supports fullscreen (8.0+)
    if (wa.isVersionAtLeast?.('8.0') && wa.requestFullscreen) {
      console.log('[TG] Requesting Telegram fullscreen...');
      wa.requestFullscreen();
      return true;
    }

    console.warn('[TG] Fullscreen not supported (need Bot API 8.0+), version:', wa.version);
    return false;
  }

  exitFullscreen(): void {
    const wa = getWa();
    if (wa?.exitFullscreen) {
      wa.exitFullscreen();
    }
  }

  isFullscreen(): boolean {
    const wa = getWa();
    if (wa) return !!wa.isFullscreen;
    return !!(document.fullscreenElement || (document as { webkitFullscreenElement?: Element }).webkitFullscreenElement);
  }

  onFullscreenChange(callback: (isFullscreen: boolean) => void): void {
    const wa = getWa();
    if (wa) {
      // Telegram event
      wa.onEvent('fullscreenChanged', () => {
        this._isFullscreen = !!wa.isFullscreen;
        callback(this._isFullscreen);
      });
    }
    // Also listen browser events (for non-Telegram)
    const handler = () => {
      const fs = !!(document.fullscreenElement || (document as { webkitFullscreenElement?: Element }).webkitFullscreenElement);
      callback(fs);
    };
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
  }

  // ─── Orientation (Telegram Bot API 8.0+) ───

  lockOrientation(): boolean {
    const wa = getWa();
    if (wa?.lockOrientation) {
      console.log('[TG] Locking orientation...');
      wa.lockOrientation();
      return true;
    }
    return false;
  }

  unlockOrientation(): void {
    const wa = getWa();
    if (wa?.unlockOrientation) {
      wa.unlockOrientation();
    }
  }

  getOrientation(): 'portrait' | 'landscape' {
    if (screen.orientation?.type?.includes('landscape')) return 'landscape';
    return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
  }

  onOrientationChange(callback: (orientation: 'portrait' | 'landscape') => void): void {
    // Screen Orientation API
    if (screen.orientation?.addEventListener) {
      screen.orientation.addEventListener('change', () => {
        setTimeout(() => callback(this.getOrientation()), 50);
      });
    }
    // Fallback
    window.addEventListener('orientationchange', () => {
      setTimeout(() => callback(this.getOrientation()), 150);
    });
  }
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TgWebApp;
    };
  }
}
